import {
  getHonorariumTemplateStorageKey,
  parseHonorariumTemplatePayload,
  u3129HonorariumTemplateRowKey,
  writeHonorariumTemplateToStorage,
  type U3129HonorariumTemplateRow,
} from '../../u3129Timesheet/tools/u3129HonorariumTemplate';
import type {
  U3147FindProjectItem,
  U3147FindSubprojectItem,
  U3147RecentProjectItem,
  U3147TaskItem,
} from '../U3147Service';
import { u3147FormatTaskLabel, u3147RecentProjectId, u3147ResolveTimerProjectId } from '../U3147Service';

export { getHonorariumTemplateStorageKey };

/** Champs U3147 persistés dans le JSON modèle U3129 (ignorés par l’API U3129, lus par U3147). */
export type U3147HonorariumTemplateRowExtension = {
  note?: string;
};

export type U3147HonorariumTemplateRow = U3129HonorariumTemplateRow & U3147HonorariumTemplateRowExtension;

export function u3147TemplateRowIdentityKey(
  row: Pick<U3147HonorariumTemplateRow, 'projectId' | 'subprojectId' | 'taskType'>,
): string {
  return u3129HonorariumTemplateRowKey(row);
}

export function u3147FormRowIdentityKey(data: {
  project: U3147FindProjectItem | null;
  subproject: U3147FindSubprojectItem | null;
  taskId: string;
}): string | null {
  const projectId = u3147ResolveTimerProjectId(data.project);
  if (!projectId) return null;
  return u3147TemplateRowIdentityKey({
    projectId,
    subprojectId: data.subproject?.id?.trim() ?? '',
    taskType: data.taskId?.trim() ?? '',
  });
}

export function u3147HonorariumTemplateRowToRecent(row: U3147HonorariumTemplateRow): U3147RecentProjectItem | null {
  const projectId = row.projectId?.trim();
  if (!projectId) return null;

  const project = row.project as U3147FindProjectItem | undefined;
  const subproject = row.subproject as U3147FindSubprojectItem | undefined;
  const subprojectId = row.subprojectId?.trim() || null;
  const taskRef = row.task as { taskCode?: string; description?: string } | undefined;

  return {
    id: projectId,
    projectId,
    subprojectId,
    taskId: row.taskType?.trim() || null,
    note: row.note?.trim() || null,
    projetNumber: project?.projetNumber,
    projetName: project?.projetName,
    customerCode: project?.customerCode,
    customerName: project?.customerName,
    includeSubProjet: project?.includeSubProjet ?? !!subprojectId,
    subProjetNumber: subproject?.subProjetNumber,
    subProjetName: subproject?.subProjetName,
    taskCode: row.taskCode?.trim() || taskRef?.taskCode,
    taskDescriptionFr: row.taskDescriptionFr ?? undefined,
    taskDescriptionEn: row.taskDescriptionEn ?? undefined,
    taskDescription: taskRef?.description,
  };
}

export function u3147ReadHonorariumTemplateRows(storageKey: string): U3147HonorariumTemplateRow[] {
  const raw = parseHonorariumTemplatePayload(localStorage.getItem(storageKey)) as U3147HonorariumTemplateRow[] | null;
  return raw ?? [];
}

export function u3147PatchHonorariumTemplateRowTaskMetadata(
  storageKey: string,
  identityKey: string,
  task: U3147TaskItem,
  lang = 'fr',
): boolean {
  const existing = u3147ReadHonorariumTemplateRows(storageKey);
  const index = existing.findIndex((item) => u3147TemplateRowIdentityKey(item) === identityKey);
  if (index < 0) return false;

  const row = existing[index]!;
  const taskType = row.taskType?.trim() || String(task.id);
  const patched: U3147HonorariumTemplateRow = {
    ...row,
    taskType,
    taskCode: task.taskCode,
    taskDescriptionFr: task.taskDescriptionFr,
    taskDescriptionEn: task.taskDescriptionEn,
    task: {
      id: taskType,
      description: u3147FormatTaskLabel(task, lang),
    },
  };

  const next = existing.map((item, i) => (i === index ? patched : item));
  return writeHonorariumTemplateToStorage(storageKey, next);
}

/** Favoris U3147 = lignes du modèle honoraires U3129 (localStorage partagé). */
export function u3147ReadHonorariumTemplateRecentProjects(storageKey: string): U3147RecentProjectItem[] {
  const rows = u3147ReadHonorariumTemplateRows(storageKey);
  return rows.flatMap((row) => {
    const item = u3147HonorariumTemplateRowToRecent(row);
    return item ? [item] : [];
  });
}

export function u3147TimerFormToTemplateRow(data: {
  project: U3147FindProjectItem | null;
  subproject: U3147FindSubprojectItem | null;
  taskId: string;
  note?: string;
  task?: U3147TaskItem | null;
  lang?: string;
}): U3147HonorariumTemplateRow | null {
  const projectId = u3147ResolveTimerProjectId(data.project);
  if (!projectId) return null;

  const subprojectId = data.subproject?.id?.trim() ?? '';
  const taskType = data.taskId?.trim() ?? '';
  const task = data.task;
  const taskDisplay = task ? u3147FormatTaskLabel(task, data.lang ?? 'fr') : '';

  return {
    projectId,
    subprojectId,
    taskType,
    project: data.project ?? undefined,
    subproject: data.subproject ?? undefined,
    task: taskType
      ? {
          id: taskType,
          description: taskDisplay || undefined,
        }
      : undefined,
    note: data.note?.trim() || undefined,
    taskCode: task?.taskCode,
    taskDescriptionFr: task?.taskDescriptionFr,
    taskDescriptionEn: task?.taskDescriptionEn,
  };
}

function u3147MergeTemplateRow(existing: U3147HonorariumTemplateRow, incoming: U3147HonorariumTemplateRow): U3147HonorariumTemplateRow {
  return {
    ...existing,
    ...incoming,
    project: incoming.project ?? existing.project,
    subproject: incoming.subproject ?? existing.subproject,
    task: incoming.task ?? existing.task,
    note: incoming.note !== undefined ? incoming.note : existing.note,
    taskCode: incoming.taskCode ?? existing.taskCode,
    taskDescriptionFr: incoming.taskDescriptionFr ?? existing.taskDescriptionFr,
    taskDescriptionEn: incoming.taskDescriptionEn ?? existing.taskDescriptionEn,
  };
}

/** Ajoute ou met à jour un favori (clé projet + sous-projet + tâche ; note mise à jour). */
export function u3147UpsertHonorariumTemplateRow(storageKey: string, row: U3147HonorariumTemplateRow): boolean {
  const existing = u3147ReadHonorariumTemplateRows(storageKey);
  const identityKey = u3147TemplateRowIdentityKey(row);
  const index = existing.findIndex((item) => u3147TemplateRowIdentityKey(item) === identityKey);
  const next =
    index >= 0
      ? existing.map((item, i) => (i === index ? u3147MergeTemplateRow(item, row) : item))
      : [...existing, row];
  return writeHonorariumTemplateToStorage(storageKey, next);
}

export function u3147RemoveHonorariumTemplateRowByKey(storageKey: string, identityKey: string): boolean {
  const existing = u3147ReadHonorariumTemplateRows(storageKey);
  const next = existing.filter((item) => u3147TemplateRowIdentityKey(item) !== identityKey);
  if (next.length === existing.length) return false;
  return writeHonorariumTemplateToStorage(storageKey, next);
}

/** Met à jour la note du favori correspondant (sans changer projet / tâche). */
export function u3147UpdateHonorariumTemplateRowNote(
  storageKey: string,
  identityKey: string,
  note: string | null | undefined,
): boolean {
  const existing = u3147ReadHonorariumTemplateRows(storageKey);
  const index = existing.findIndex((item) => u3147TemplateRowIdentityKey(item) === identityKey);
  if (index < 0) return false;
  const normalized = note?.trim() || undefined;
  const current = existing[index]!.note?.trim() || undefined;
  if (current === normalized) return false;
  const next = existing.map((item, i) => (i === index ? { ...item, note: normalized } : item));
  return writeHonorariumTemplateToStorage(storageKey, next);
}

export function u3147IsIdentityKeyInTemplate(storageKey: string, identityKey: string): boolean {
  return u3147ReadHonorariumTemplateRows(storageKey).some(
    (item) => u3147TemplateRowIdentityKey(item) === identityKey,
  );
}

export function u3147RecentProjectChipKey(item: U3147RecentProjectItem): string {
  const pid = u3147RecentProjectId(item) ?? '';
  const sp = item.subprojectId?.trim() ?? '';
  const tid = item.taskId?.trim() ?? '';
  return u3147TemplateRowIdentityKey({ projectId: pid, subprojectId: sp, taskType: tid });
}
